import { useState, useEffect, useCallback } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Alert, AlertDescription } from '../components/ui/alert';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  QrCode, 
  Clock, 
  AlertCircle, 
  CheckCircle2, 
  RefreshCw,
  Shield,
  Users,
  Calendar
} from 'lucide-react';
import { useAuth } from '../lib/AuthContext';
import { toast } from 'sonner';
import { generateVerificationToken, getRecentVerifications } from '../lib/api/verification';
import type { ProfileVerification } from '../lib/types';
import { copyToClipboard } from '../lib/utils/clipboard';

interface VerificationToken {
  token: string;
  expiresAt: string;
}

interface VerifyQRProps {
  onNavigate?: (page: string) => void;
}

export function VerifyQR({ onNavigate }: VerifyQRProps) {
  const { user, profile } = useAuth();
  const [token, setToken] = useState<VerificationToken | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const [recentVerifications, setRecentVerifications] = useState<ProfileVerification[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check if user is clinic
  const isClinic = profile?.role === 'clinic';
  const clinicId = profile?.clinic_id;

  // Load recent verifications
  const loadRecentVerifications = useCallback(async () => {
    if (!clinicId) return;
    
    try {
      setLoadingHistory(true);
      const verifications = await getRecentVerifications(clinicId, 5);
      setRecentVerifications(verifications);
    } catch (error: any) {
      console.error('Error loading verifications:', error);
      toast.error('Failed to load verification history');
    } finally {
      setLoadingHistory(false);
    }
  }, [clinicId]);

  // Load history on mount
  useEffect(() => {
    if (isClinic && clinicId) {
      loadRecentVerifications();
    } else {
      setLoadingHistory(false);
    }
  }, [isClinic, clinicId, loadRecentVerifications]);

  // Countdown timer
  useEffect(() => {
    if (!token) {
      setTimeRemaining(null);
      return;
    }

    const updateTimer = () => {
      const now = Date.now();
      const expiresAt = new Date(token.expiresAt).getTime();
      const remaining = Math.max(0, Math.floor((expiresAt - now) / 1000));
      
      setTimeRemaining(remaining);

      if (remaining === 0) {
        setError('Token expired. Please generate a new QR code.');
        setToken(null);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [token]);

  // Generate new verification token
  const handleGenerateToken = async () => {
    if (!user || !clinicId) {
      toast.error('Please sign in as a clinic user');
      return;
    }

    // Check if token already exists and is not expired
    if (token && timeRemaining && timeRemaining > 0) {
      toast.error('A QR code is already active. Please wait for it to expire or use it.');
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      const newToken = await generateVerificationToken(clinicId);
      setToken(newToken);
      toast.success('QR code generated successfully!');
    } catch (error: any) {
      console.error('Error generating token:', error);
      
      // Handle specific errors
      if (error.message?.includes('reuse')) {
        setError('Cannot reuse tokens. Please wait for the current token to expire.');
        toast.error('Token reuse not allowed');
      } else if (error.message?.includes('expired')) {
        setError('Previous token has expired. Generating new token...');
        // Retry once
        try {
          const newToken = await generateVerificationToken(clinicId);
          setToken(newToken);
          setError(null);
          toast.success('New QR code generated');
        } catch (retryError: any) {
          setError('Failed to generate verification token');
          toast.error('Generation failed');
        }
      } else {
        setError(error.message || 'Failed to generate verification token');
        toast.error('Generation failed');
      }
    } finally {
      setIsGenerating(false);
    }
  };

  // Format time remaining
  const formatTimeRemaining = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Format date for table
  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Access control
  if (!isClinic) {
    return (
      <div className="min-h-screen pt-24 pb-20 px-4 sm:px-6 lg:px-8 gradient-mesh">
        <div className="max-w-2xl mx-auto">
          <Card className="p-8 text-center glass-card">
            <Shield className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-2xl mb-3">Clinic Access Required</h2>
            <p className="text-muted-foreground mb-6">
              This page is only accessible to verified clinic users.
            </p>
            <Button
              onClick={() => onNavigate && onNavigate('home')}
              variant="outline"
            >
              Return Home
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-20 px-4 sm:px-6 lg:px-8 gradient-mesh">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl mb-3">Generate Verification QR Code</h1>
          <p className="text-xl text-muted-foreground">
            Create a QR code for your patient to scan and verify their account
          </p>
        </motion.div>

        {/* Error Alert */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="mb-6"
            >
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            </motion.div>
          )}
        </AnimatePresence>

        {/* QR Code Generation Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
        >
          <Card className="p-8 glass-card mb-8">
            <div className="flex flex-col items-center">
              {!token ? (
                // Generate Button State
                <div className="text-center w-full">
                  <div className="w-64 h-64 mx-auto mb-6 rounded-2xl bg-muted/30 flex items-center justify-center border-2 border-dashed border-border">
                    <QrCode className="w-24 h-24 text-muted-foreground" />
                  </div>
                  <Button
                    onClick={handleGenerateToken}
                    disabled={isGenerating}
                    className="gradient-green hover:opacity-90 transition-opacity px-8 h-12"
                    size="lg"
                  >
                    {isGenerating ? (
                      <>
                        <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <QrCode className="w-5 h-5 mr-2" />
                        Generate 10-min QR
                      </>
                    )}
                  </Button>
                  <p className="text-sm text-muted-foreground mt-4">
                    Click to generate a secure verification code
                  </p>
                </div>
              ) : (
                // QR Code Display State
                <div className="text-center w-full">
                  <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: 'spring', stiffness: 200 }}
                    className="inline-block p-6 bg-white rounded-2xl shadow-xl mb-6"
                  >
                    <QRCodeSVG
                      value={token.token}
                      size={256}
                      level="H"
                      includeMargin={true}
                      imageSettings={{
                        src: '', // Could add logo here
                        height: 0,
                        width: 0,
                        excavate: false,
                      }}
                    />
                  </motion.div>

                  {/* Timer */}
                  <div className="mb-6">
                    <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary/10 border border-primary/20">
                      <Clock className="w-5 h-5 text-primary" />
                      <span className="text-2xl font-mono text-primary">
                        {timeRemaining !== null ? formatTimeRemaining(timeRemaining) : '0:00'}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-2">
                      Time remaining
                    </p>
                  </div>

                  {/* Token Details */}
                  <div className="space-y-3 mb-6">
                    <div className="p-3 rounded-lg bg-muted/30 border border-border/50">
                      <p className="text-xs text-muted-foreground mb-1">Verification Token</p>
                      <p className="font-mono text-sm break-all">{token.token.slice(0, 32)}...</p>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3 justify-center">
                    <Button
                      onClick={async () => {
                        try {
                          await copyToClipboard(token.token);
                          toast.success('Token copied to clipboard');
                        } catch (error) {
                          console.error('Failed to copy token:', error);
                          toast.error('Failed to copy token. Please copy manually.');
                        }
                      }}
                      variant="outline"
                    >
                      Copy Token
                    </Button>
                    <Button
                      onClick={() => {
                        setToken(null);
                        setError(null);
                      }}
                      variant="outline"
                    >
                      Clear QR Code
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </Card>
        </motion.div>

        {/* Recent Verifications Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
        >
          <Card className="p-6 glass-card">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl mb-1">Recent Verifications</h2>
                <p className="text-sm text-muted-foreground">
                  Last 5 successful patient verifications
                </p>
              </div>
              <Button
                onClick={loadRecentVerifications}
                variant="outline"
                size="sm"
                disabled={loadingHistory}
              >
                {loadingHistory ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
              </Button>
            </div>

            {loadingHistory ? (
              <div className="text-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-3 text-muted-foreground" />
                <p className="text-muted-foreground">Loading verification history...</p>
              </div>
            ) : recentVerifications.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
                <p className="text-muted-foreground">No verifications yet</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Generated QR codes will appear here once scanned
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Patient ID</TableHead>
                      <TableHead>Method</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Verified At</TableHead>
                      <TableHead>Notes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recentVerifications.map((verification) => (
                      <TableRow key={verification.id}>
                        <TableCell className="font-mono text-sm">
                          {verification.user_id.slice(0, 8)}...
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="capitalize">
                            {verification.verification_method.replace('_', ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={verification.verified ? 'default' : 'secondary'}
                            className="capitalize"
                          >
                            {verification.verified && (
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                            )}
                            {verification.verified ? 'Verified' : 'Pending'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3 h-3 text-muted-foreground" />
                            {formatDate(verification.verified_at)}
                          </div>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">
                          {verification.notes || '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </Card>
        </motion.div>

        {/* Info Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="mt-6 text-center"
        >
          <Alert>
            <Shield className="h-4 w-4" />
            <AlertDescription>
              <strong>Security Note:</strong> Each QR code is valid for 10 minutes only and can only be used once. 
              Tokens are encrypted and expire automatically.
            </AlertDescription>
          </Alert>
        </motion.div>
      </div>
    </div>
  );
}